import FetchAiCostLimitAlerts from '@Application/AiCostLimits/Alerts/Queries/FetchAiCostLimitAlerts';
import fetchAiCostLimitAlerts from '@Application/AiCostLimits/Alerts/fetchAiCostLimitAlerts';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import useSubjectNameResolver from '@Guidelines/Shell/AiCostLimitBanner/Hooks/useSubjectNameResolver';
import resolveAlertSentence from '@Guidelines/Shell/AiCostLimitBanner/Helpers/resolveAlertSentence';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import { css } from '@emotion/react';
import * as React from 'react';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Button, Message } from '@verclam/ui-kit';

const messageStyle = css`
	margin: 0 auto;
	display: flex;
	width: fit-content;
`;

const contentStyle = css`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	flex-wrap: wrap;
`;

const sentencesStyle = css`
	flex: 1 1 auto;
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
`;

const actionButtonStyle = css`
	min-height: 44px;
`;

/**
 * Alerts of the caller's cost limits, read once per page load: a limit crossed during the session
 * shows up at the next refresh. Loading, failure and an empty list render nothing — an information
 * banner never blocks the application. "Masquer" hides it until the page is refreshed.
 */
const AiCostLimitBanner: React.FC = () => {
	const dispatch = useDispatch();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;
	const [isHidden, setIsHidden] = React.useState<boolean>(false);

	const { data: alerts } = useStoreQuery({
		selector: (state) => state.aiCostLimitAlertsStore.alerts,
		stopOn: (value) => value !== null && value.accountId === accountId,
		query: () => {
			if (accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchAiCostLimitAlerts(new FetchAiCostLimitAlerts(accountId)));
		},
		deps: [accountId],
	});

	const findName = useSubjectNameResolver(alerts?.list ?? null, accountId);

	const handleHide = React.useCallback((): void => {
		setIsHidden(true);
	}, []);

	const banner = React.useMemo(() => {
		if (alerts === null || alerts.accountId !== accountId) {
			return null;
		}

		const sentences = alerts.list.flatMap((alert) => {
			const sentence = resolveAlertSentence(alert, findName);
			return sentence === null ? [] : [{ key: alert.limitId, sentence }];
		});

		if (sentences.length === 0) {
			return null;
		}

		return {
			sentences,
			severity: alerts.list.some((alert) => alert.isBlocking) ? ('error' as const) : ('warn' as const),
		};
	}, [alerts, accountId, findName]);

	return (
		<ConditionalLoader data={isHidden ? null : banner}>
			{(visible) => (
				<Message
					severity={visible.severity}
					css={messageStyle}
					content={
						<div css={contentStyle}>
							<div css={sentencesStyle}>
								{visible.sentences.map(({ key, sentence }) => (
									<span key={key}>{sentence}</span>
								))}
							</div>
							<Button
								type="button"
								label="Masquer"
								text
								css={actionButtonStyle}
								onClick={handleHide}
							/>
						</div>
					}
				/>
			)}
		</ConditionalLoader>
	);
};

export default AiCostLimitBanner;
